import { Box, Card, CardContent, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DateDisplay, DowJonesBrand, MonoText, RefinitivBrand, StatusLabel, Title } from 'app/components';
import { StatusMaps } from 'app/constants';
import clsx from 'clsx';
import moment from 'moment';
import React, { useState } from 'react';
import { PreviewMatchList, TableDetailPreview, TablePreview } from './components';

const useStyles = makeStyles((theme) => ({
  root: {
    '@media print': { boxShadow: 'none' },
    '-webkit-print-color-adjust': 'exact !important',
    'color-adjust': 'exact !important',
  },
  noPrint: {
    '@media print': { display: 'none' },
  },
  printContent: {
    padding: theme.spacing(3, 3, 8),
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2, 2, 6) },
    '@media print': { margin: 'none' },
    component: 'none',
  },
  logo: { height: 82 },
  frame: {
    width: '80%',
    'margin-left': 'auto',
    'margin-right': 'auto',
  },
  marginTop: {
    marginTop: theme.spacing(2),
    '@media print': { margin: 'none' },
  },
  table: { border: '2px solid #f5f5f5' },
  tableHeader: { backgroundColor: 'red' },
  button: {
    float: 'right',
    height: 30,
    marginLeft: theme.spacing(2),
    '@media print': { margin: 'none', height: 0, display: 'none' },
  },
}));

const getTableData = (key, value, extra) => ({ key, value, extra });

const getScreen = ({ screen }) => [
  getTableData('Date', screen.submit_at, {
    wrapper: () => <DateDisplay date={screen.submit_at} dateFormat={'DD-MMM-YYYY HH:mm'} />,
  }),
  getTableData('Screen ID', screen.reference, { wrapper: MonoText }),
  getTableData('Type', screen.type),
  getTableData('Status', screen.status, {
    wrapper: ({ children }) => <StatusLabel variantMap={StatusMaps.Screen}>{children}</StatusLabel>,
  }),
  getTableData('Match Count', screen.results_count),
];

const getCase = ({ case: caseRef }) => [
  getTableData('Name', caseRef.label),
  getTableData('Case ID', caseRef.reference, { wrapper: MonoText }),
  getTableData('Status', caseRef.status, {
    wrapper: ({ children }) => <StatusLabel variantMap={StatusMaps.Case}>{children}</StatusLabel>,
  }),
];

const getMatch = (match) => {
  if (match.screen_provider_id === 1) {
    return [
      getTableData('Match ID', match.reference, { wrapper: MonoText }),
      getTableData('Name', match.name),
      getTableData('Match Rating', match.match_rating),
      getTableData('Match Type', match.match_type),
      getTableData('Gender', match.gender),
      getTableData('Profile Retrieve Date', match.profile_retrieved_at, {
        wrapper: () => <DateDisplay date={match.profile_retrieved_at} />,
      }),
    ];
  } else {
    return [
      getTableData('Match ID', match.reference, { wrapper: MonoText }),
      getTableData('Name', match.name),
      getTableData('Match Rating', match.match_rating),
      getTableData('Match Type', match.match_type),
      getTableData('Match Score', match.score),
      getTableData('Gender', match.gender),
      getTableData('Profile Retrieve Date', match.profile_retrieved_at, {
        wrapper: () => <DateDisplay date={match.profile_retrieved_at} />,
      }),
    ];
  }
};

// ---------------------------------------------------------------------------
// Old helpers (kept for Refinitiv / ComplyAdvantage paths)
// ---------------------------------------------------------------------------
const cleanComment = (comment) => {
  const comments = comment?.split('\n');
  const conciseComments = comments?.slice(3, comments.length);
  const listComments = conciseComments?.map((c, i) => (
    <React.Fragment key={i}>
      {c}
      <br />
    </React.Fragment>
  ));
  return <>{listComments}</>;
};

const cleanRelationships = (relationships) => {
  const listRelationships = relationships?.map((c, i) => (
    <React.Fragment key={i}>
      {c.name_detail.first_name} {c.name_detail.middle_name} {c.name_detail.surname} ({c.icon_hints}) ({c.connection_type}),{' '}
    </React.Fragment>
  ));
  return <>{listRelationships}</>;
};

const cleanReports = (reports) => {
  const listReports = reports.map((r, i) => (
    <React.Fragment key={i}>
      {r.title || r.external_link} <br />
    </React.Fragment>
  ));
  return <>{listReports}</>;
};

// ---------------------------------------------------------------------------
// Regtank helpers
// ---------------------------------------------------------------------------
const cleanRegtankProfileNotes = (profileNotes = []) => {
  if (!profileNotes.length) return '-';
  return (
    <>
      {profileNotes.map((note, i) => (
        <React.Fragment key={i}>
          {note.split('\n').map((line, j) => (
            <React.Fragment key={j}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

const cleanRegtankAssociates = (associates = []) => {
  if (!associates.length) return '-';
  return (
    <>
      {associates.map((a, i) => (
        <React.Fragment key={i}>
          {a.name} ({a.type}
          {a.keywords?.length ? `, ${a.keywords.join(', ')}` : ''}){i < associates.length - 1 ? ', ' : ''}
        </React.Fragment>
      ))}
    </>
  );
};

const cleanRegtankMedia = (media = []) => {
  if (!media.length) return '-';
  return (
    <>
      {media.map((r, i) => (
        <React.Fragment key={i}>
          <a href={r.externalLink} target="_blank" rel="noopener noreferrer">
            {r.title || r.externalLink}
          </a>
          <br />
        </React.Fragment>
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// getMatchDetail — handles all three providers
// ---------------------------------------------------------------------------
const getMatchDetail = ({ screen }, { details, names },isOrganisation) => {
  let arr = [];

  if (screen.screen_provider_id === 1) {
    // ── Refinitiv ──────────────────────────────────────────────────────────
    const detailList = details?.details || [];
    detailList.forEach((detail) => {
      arr.push(getTableData(detail.title, detail.text));
    });
  } else if (screen.screen_provider_id === 2) {
    // ── ComplyAdvantage ────────────────────────────────────────────────────
    const watchlist = details?.watchlist;
    if (watchlist) {
      const profile = cleanComment(watchlist.comment_details?.si_comment || watchlist.comment_details?.list_comment);
      arr.push(getTableData('Profile', profile));
      const relationships = cleanRelationships(names?.connection_details);
      arr.push(getTableData('Identification', relationships));
      const reports = cleanReports(watchlist.sources || []);
      arr.push(getTableData('Articles and Reports', reports));
    }
  } else {
    const person = details?.detail?.profile?.person || {};
    const media = details?.media || [];
    const lists = details?.lists || {};
    const peps = details?.peps || {};
    const amlTypes = details?.aml_types || [];

    // ── Identity ──────────────────────────────────────────────────────────
    arr.push(getTableData('Name', details?.detail?.profile?.matching_name || '-'));

    if (!isOrganisation) {
      arr.push(getTableData('Aliases', person.aliases?.length ? person.aliases.join(', ') : '-'));
      arr.push(getTableData('Gender', lists.gender || '-'));
      arr.push(getTableData('Nationality', lists.nationality ? `${lists.nationality}${lists.nationalityCode ? ` (${lists.nationalityCode})` : ''}` : '-'));
      arr.push(getTableData('Jurisdiction', peps.jurisdiction || '-'));
      arr.push(getTableData('Country of Residence', lists.countryOfResidence || '-'));
      arr.push(getTableData('Place of Birth', lists.placeOfBirth || '-'));
      arr.push(getTableData('Deceased', peps.deceased ? `Yes${peps.dayOfDeath || peps.monthOfDeath || peps.yearOfDeath ? ` (${[peps.dayOfDeath, peps.monthOfDeath, peps.yearOfDeath].filter(Boolean).join('/')})` : ''}` : 'No'));

      if (person.addresses?.length) {
        arr.push(getTableData('Address', person.addresses.map((a) => [a.city, a.stateOrRegion, a.countryOrTerritory].filter(Boolean).join(', ')).join('; ')));
      }

      if (person.reportedAllegation) {
        arr.push(getTableData('Reported Allegation', person.reportedAllegation));
      }

      // ── Date of Birth ────────────────────────────────────────────────────
      arr.push(getTableData('Date of Birth', person.dates_of_birth?.length ? person.dates_of_birth.map((d) => d.value).join(', ') : '-'));

      if (peps.otherRecordedDatesOfBirth?.length) {
        arr.push(getTableData('Other Recorded DOB', peps.otherRecordedDatesOfBirth.map((d) => [d.day, d.month, d.year].filter(Boolean).join('/')).join(', ')));
      }
    }

    // ── Organisation fields ───────────────────────────────────────────────
    if (isOrganisation) {
      if (lists.businessName) arr.push(getTableData('Business Name', lists.businessName));
      if (lists.countryOfIncorporation) arr.push(getTableData('Country of Incorporation', lists.countryOfIncorporation));
      if (peps.dayOfIncorporation) arr.push(getTableData('Date of Incorporation', `${peps.dayOfIncorporation}-${peps.monthOfIncorporation}-${peps.yearOfIncorporation}`));
      if (peps.natureOfBusiness?.length) arr.push(getTableData('Nature of Business', peps.natureOfBusiness.join(', ')));
      if (peps.identificationDetails?.length) arr.push(getTableData('Identification Details', peps.identificationDetails.map((id) => `${id.type}: ${id.value}`).join(', ')));
    }

    // ── Risk Type ─────────────────────────────────────────────────────────
    arr.push(getTableData('Risk Type', amlTypes.join(', ') || '-'));

    // ── Risk Classifications ──────────────────────────────────────────────
    if (lists.referenceList?.length) {
      const refSummary = (
        <>
          {lists.referenceList.map((ref, i) => {
            const listItems = [...(ref.sanctions || []).map((s) => ({ ...s })), ...(ref.otherOfficialList || []).map((o) => ({ ...o })), ...(ref.specialInterestPersonList || []).map((s) => ({ ...s }))];
            return (
              <React.Fragment key={i}>
                <strong>{ref.keyword}</strong> – {ref.category} ({ref.status})<br />
                {listItems.map((item, j) => (
                  <React.Fragment key={j}>
                    &nbsp;&nbsp;• {item.name}
                    {item.from?.year ? ` (Since ${[item.from.day, item.from.month, item.from.year].filter(Boolean).join('/')}${item.to ? ` – ${[item.to.day, item.to.month, item.to.year].filter(Boolean).join('/')}` : ' – present'})` : ''}
                    <br />
                    {item.sources?.map((src, k) => (
                      <React.Fragment key={k}>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        <a href={src} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
                          {src}
                        </a>
                        <br />
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </>
      );
      arr.push(getTableData('Risk Classifications', refSummary));
    }

    // ── Identification ────────────────────────────────────────────────────
    if (lists.identificationInformationList?.length) {
      arr.push(getTableData('Identification', lists.identificationInformationList.map((id) => `${id.type}: ${id.number}`).join('  ·  ')));
    }

    // ── Roles ─────────────────────────────────────────────────────────────
    const allRoles = (lists.referenceList || []).flatMap((ref) => [...(ref.primaryOccupation ? [{ ...ref.primaryOccupation, current: true }] : []), ...(ref.previousRoles || []).map((r) => ({ ...r, current: false }))]);
    if (allRoles.length) {
      const rolesSummary = (
        <>
          {allRoles.map((role, i) => (
            <React.Fragment key={i}>
              {role.current ? '● ' : '○ '}
              <strong>{role.title}</strong> ({[role.from?.month, role.from?.year].filter(Boolean).join('/')}
              {role.to ? ` – ${[role.to.month, role.to.year].filter(Boolean).join('/')}` : ' – present'}){role.category ? ` · ${role.category}` : ''}
              <br />
            </React.Fragment>
          ))}
        </>
      );
      arr.push(getTableData('Roles', rolesSummary));
    }

    // ── Connections ───────────────────────────────────────────────────────
    const connections = lists.connections?.length ? lists.connections : person.associates;
    arr.push(getTableData('Associates / Connections', cleanRegtankAssociates(connections)));

    // ── Media ─────────────────────────────────────────────────────────────
    arr.push(getTableData('Articles and Reports', cleanRegtankMedia(media)));

    // ── Profile Notes ─────────────────────────────────────────────────────
    arr.push(getTableData('Profile Notes', cleanRegtankProfileNotes(person.profileNotes)));
  }

  return arr;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const DownloadRegtankPreview = (props) => {
  const { matchList, detail, profile, cancelPrint, className, isOrganisation, ...rest } = props;
  const [screenData] = useState(getScreen(detail));
  const [caseData] = useState(getCase(detail.screen));
  const [matchData] = useState(getMatch(detail));
  const [matchDetailData] = useState(getMatchDetail(detail, profile, isOrganisation));
  const classes = useStyles();

  const getButtons = () => [
    { space: 3, onClick: () => cancelPrint(), text: 'Cancel' },
    { space: 4, onClick: () => window.print(), text: 'Print' },
    { space: 5, holder: true },
  ];

  const renderProviderBrand = () => {
    if (detail.screen.screen_provider_id === 1) return <RefinitivBrand />;
    if (detail.screen.screen_provider_id === 2) return <DowJonesBrand />;
    return <Typography variant="caption">Powered by Regtank</Typography>;
  };

  return (
    <Box className={clsx(classes.root, className)}>
      <Title prefix="Print preview" className={classes.noPrint} floatButtons={getButtons()} />
      <Card className={clsx(classes.root, classes.frame, className)}>
        <CardContent className={classes.printContent}>
          <Grid container justify="space-between" {...rest}>
            <Grid item>
              <img alt="Brand" className={classes.logo} src="/images/traxx_logo.svg" />
              {renderProviderBrand()}
            </Grid>
          </Grid>

          <Grid container justify="space-between">
            <Grid item>
              <MonoText>
                <Typography variant="h4" fontWeight="bold">
                  Screening Report
                </Typography>
              </MonoText>
            </Grid>
          </Grid>

          <Grid className={classes.marginTop} container justify="space-between">
            <Grid item>
              <MonoText>{`Report generated on ${moment().format('DD MMM YYYY, HH:mm')}`}</MonoText>
            </Grid>
          </Grid>

          <Grid className={classes.marginTop} container spacing={2} justify="space-between">
            <Grid item xs={6} sm={6} lg={6}>
              <TablePreview header="Match profile" data={matchData} />
            </Grid>
            <Grid item xs={6} sm={6} lg={6}>
              <TablePreview header="Case details" data={caseData} />
            </Grid>
          </Grid>

          <Grid className={classes.marginTop} container spacing={2} justify="space-between">
            <Grid item xs={12} sm={12} lg={12}>
              <TablePreview header="Screen details" data={screenData} />
            </Grid>
          </Grid>

          <Grid className={classes.marginTop} container spacing={2} justify="space-between">
            <Grid item xs={12} sm={12} lg={12}>
              <TableDetailPreview header="Match detail" data={matchDetailData} />
            </Grid>
          </Grid>

          {matchList && (
            <Grid className={classes.marginTop} container spacing={2} justify="space-between">
              <Grid item xs={12} sm={12} lg={12}>
                <PreviewMatchList header="All matches" matchList={matchList} />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DownloadRegtankPreview;
